import { forwardRef } from 'preact/compat'
import { BRAND_CLASSES } from '@/constants/theme'

export interface CheckboxProps {
    /** Field label displayed next to the checkbox */
    label?: string
    /** Secondary description text displayed below the label */
    description?: string
    /** Whether field is required */
    required?: boolean
    /** Field name for form registration */
    name: string
    /** Error message to display */
    error?: string
    /** Custom class name for container */
    containerClassName?: string
    /** onChange handler */
    onChange?: (e: Event) => void
    /** onBlur handler */
    onBlur?: (e: Event) => void
    /** Additional props passed to input */
    [key: string]: unknown
}

/**
 * Common Checkbox component for boolean form fields.
 * Integrates with React Hook Form via forwardRef.
 *
 * @example
 * ```tsx
 * <Checkbox
 *   label="Require confirmation"
 *   description="Users must explicitly confirm"
 *   error={errors.requireConfirmation?.message}
 *   {...register('requireConfirmation')}
 * />
 * ```
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    ({ label, description, required, error, containerClassName, ...props }, ref) => {
        return (
            <div className={containerClassName}>
                <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                        ref={ref}
                        type="checkbox"
                        {...props}
                        className={`mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600 ${BRAND_CLASSES.FOCUS.PRIMARY} text-primary-600 dark:text-primary-500`}
                    />
                    {(label || description) && (
                        <div className="flex-1">
                            {label && (
                                <span className="text-sm font-medium text-slate-900 dark:text-white">
                                    {label}
                                    {required && <span className={BRAND_CLASSES.INPUT.REQUIRED}> *</span>}
                                </span>
                            )}
                            {description && (
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    {description}
                                </p>
                            )}
                        </div>
                    )}
                </label>
                {error && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>
                        {error}
                    </p>
                )}
            </div>
        )
    }
)

Checkbox.displayName = 'Checkbox'
